import React, { useState, useMemo, forwardRef, useImperativeHandle, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import ConfirmationModal from "../../../../utils/ConfirmationModal";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const REASON_CODE_OPTIONS = {
  EXCLUSION: [
    "Duplicate",
    "Animal/In-vitro",
    "Population not of interest",
    "Outcomes not of interest",
    "Study designs",
    "Language not of interest",
    "Publication",
    "Intervention not of interest",
    "Comparator not of interest",
    "Outside date range",
    "Setting or geography not of interest",
    "Full text not retrievable",
    "Insufficient data reported",
    "Other",
  ],
  INCLUSION: [
    "Population of interest",
    "Intervention of interest",
    "Comparator of interest",
    "Outcomes of interest",
    "Study design of interest",
    "Timeframe of interest",
    "Setting or geography of interest",
  ],
};

const CUSTOM_CODE = "__custom__";

// Screening order for exclusions: objective, title/abstract-level screens
// first, then PICOS, then anything that needs the full text. 
const EXCLUSION_ORDER = [
  "Duplicate",
  "Publication",
  "Study designs",
  "Animal/In-vitro",
  "Language not of interest",
  "Outside date range",
  "Population not of interest",
  "Intervention not of interest",
  "Comparator not of interest",
  "Outcomes not of interest",
  "Setting or geography not of interest",
  "Insufficient data reported",
  "Full text not retrievable",
];

const INCLUSION_ORDER = [
  "Population of interest",
  "Intervention of interest",
  "Comparator of interest",
  "Outcomes of interest",
  "Study design of interest",
  "Timeframe of interest",
  "Setting or geography of interest",
];

const orderRank = (r) => {
  const list = r.type === "EXCLUSION" ? EXCLUSION_ORDER : INCLUSION_ORDER;
  const i = list.indexOf((r.reason_code || "").trim());
  // Unknown or AI-coined codes sit just before "Other"; "Other" goes last.
  if ((r.reason_code || "").trim() === "Other") return list.length + 2;
  return i === -1 ? list.length + 1 : i;
};

// Exclusions are applied in order and evaluated first, so they lead.
const sortCriteria = (rowsInput) =>
  [...rowsInput].sort((a, b) => {
    if (a.type !== b.type) return a.type === "EXCLUSION" ? -1 : 1;
    return orderRank(a) - orderRank(b);
  });

const mergeAndSort = (rowsInput) => sortCriteria(mergeByReasonCode(rowsInput));

// Merge criteria regenerated from an updated protocol INTO the existing rows,
// instead of replacing them. Replacing dropped the saved ids, so Save tried to
// delete criteria that screening decisions still cite and was refused.
//   - same type + reason_code as an existing row → the new reason text is
//     appended to that row (parts already present are skipped); the row keeps
//     its saved id, and screening flags are OR-ed
//   - a new code, or a reason with no code that isn't already listed → added
//     as a new row (no id, so Save inserts it)
//   - existing rows are never removed
const normText = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const reasonParts = (s) => (s || "").split(";").map((p) => p.trim()).filter(Boolean);

const mergeGeneratedIntoExisting = (existingRows, generatedRows) => {
  const result = existingRows.map((r) => ({ ...r }));
  const byCode = new Map();
  result.forEach((r, i) => {
    if (r.reason_code) byCode.set(`${r.type}|||${normText(r.reason_code)}`, i);
  });
  const knownReasons = new Set(result.map((r) => normText(r.reason)));

  for (const g of generatedRows) {
    const key = g.reason_code ? `${g.type}|||${normText(g.reason_code)}` : null;
    const idx = key != null ? byCode.get(key) : undefined;

    if (idx !== undefined) {
      const target = result[idx];
      const have = new Set(reasonParts(target.reason).map(itemKey));
      // The Duplicate code keeps its own text; nothing is appended to it.
      const extra =
        normText(target.reason_code) === "duplicate"
          ? []
          : reasonParts(g.reason).filter((p) => !have.has(itemKey(p)));
      result[idx] = {
        ...target,
        reason: [target.reason?.trim(), ...extra].filter(Boolean).join("; "),
        abstract_screening: target.abstract_screening || g.abstract_screening,
        full_text_screening: target.full_text_screening || g.full_text_screening,
      };
      continue;
    }

    if (!g.reason_code && knownReasons.has(normText(g.reason))) continue;

    // Genuinely new criterion. No db_id: an id the backend attached by position
    // may already belong to an existing row in this list.
    result.push({ ...g, db_id: null });
    if (key != null) byCode.set(key, result.length - 1);
    knownReasons.add(normText(g.reason));
  }
  return result;
};

// /generate-criteria response → form rows. The backend maps each generated
// criterion to the saved row it continues (c.id); keeping it means Save
// updates that row instead of deleting it.
const toGeneratedRows = (criteria = []) =>
  criteria.map((c) => ({
    id: makeId(),
    db_id: c.id ?? null,
    order_id: c.order_id ?? null,
    type: c.criteria_type,
    reason_code: c.reason_code || null,
    reason: c.reason || "",
    abstract_screening: !!c.abstract_screening,
    full_text_screening: !!c.full_text_screening,
  }));

const makeRow = (type = "EXCLUSION") => ({
  id: makeId(),
  db_id: null,
  order_id: null,
  type, // "EXCLUSION" | "INCLUSION"
  reason_code: null,
  reason: "",
  abstract_screening: true,
  full_text_screening: true,
});

// Merge rows that share the same (type + reason_code).
// Rows without a reason_code are never merged.
// reason texts are joined with "; ", screening flags use OR logic.
const mergeByReasonCode = (rowsInput) => {
  const merged = [];
  const keyMap = new Map(); // `${type}|||${reason_code}` → index in merged

  for (const r of rowsInput) {
    if (!r.reason_code) {
      merged.push(r);
      continue;
    }
    const key = `${r.type}|||${r.reason_code}`;
    if (keyMap.has(key)) {
      const idx = keyMap.get(key);
      const existing = merged[idx];
      const a = (existing.reason || "").trim();
      // Only the parts of b that a does not already say.
      const aKeys = new Set(reasonParts(a).map(itemKey));
      const b = reasonParts(r.reason).filter((p) => !aKeys.has(itemKey(p))).join("; ");
      merged[idx] = {
        ...existing,
        // Keep a saved id if either row has one, so the merge doesn't turn an
        // existing criterion into a delete.
        db_id: existing.db_id ?? r.db_id ?? null,
        reason: a && b ? `${a}; ${b}` : a || b,
        abstract_screening: existing.abstract_screening || r.abstract_screening,
        full_text_screening: existing.full_text_screening || r.full_text_screening,
      };
    } else {
      keyMap.set(key, merged.length);
      merged.push({ ...r });
    }
  }
  return merged;
};

// ── Repeated-information cleanup ──────────────────────────────────────────
// Same rules as the backend safety net (slr_form_service._dedupe_criteria_content):
//   - the Duplicate code holds no "same trial / dataset / cohort" wording
//   - a condition is listed once inside a row, and under one code only
//     (the code it belongs to; otherwise rows that already existed win)
//   - study-design items never sit under "Publication" and vice versa — the
//     protocol may keep both in one row, but each code gets only its own
//   - an exclusion never repeats an inclusion word for word
//   - a row is never emptied
const DUPLICATE_REASON = "Duplicate records";
const LINKED_PUBLICATION_RE =
  /same\s+(?:trial|study|studies|data\s*set|dataset|cohort|population|patients|participants)|multiple\s+(?:publications?|reports?)|secondary\s+(?:publications?|reports?|analys[ie]s)|companion\s+(?:publications?|reports?|papers?)|linked\s+(?:publications?|reports?)|overlapping\s+(?:populations?|data|cohorts?|patients)/i;
const ITEM_OWNERS = [
  ["publication", /conference|abstract[- ]only|poster|letter|editorial|comment|errat|corrigend|retract|preprint|thesis|dissertation|\bnotes?\b/i],
  ["study design", /case report|case series|review|meta[- ]?analys|observational|cohort|cross[- ]sectional|case[- ]control|single[- ]arm|non[- ]?randomi|non[- ]?rcts?\b|\bdesigns?\b/i],
  ["animal", /animal|in[- ]vitro|pre[- ]?clinical|\bmice\b|\bmouse\b|\brats?\b|cell line/i],
  ["language", /language|english/i],
  ["date", /\b(?:19|20)\d{2}\b|date range|time ?frame/i],
];
const OWNER_RX = Object.fromEntries(ITEM_OWNERS);
const PART_SEP_RE = /\s*[;\n]\s*/g;
const ITEM_SEP_RE = /\s*,(?!\d)\s*(?:and\/or|or|and)\s+|\s*,(?!\d)\s*|\s+(?:and\/or|or)\s+/gi;

// Comparison key: case, punctuation, hyphens and plural "s" ignored.
function itemKey(text) {
  const t = String(text || "")
    .toLowerCase()
    .trim()
    .replace(/^(?:and\/or|or|and)\s+/, "")
    .replace(/[^\p{L}\p{N}_<>≤≥=%+.]+/gu, " ");
  return t
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w))
    .join(" ")
    .replace(/^[ .]+|[ .]+$/g, "");
}

const codeOwner = (code) => {
  const c = normText(code);
  if (c.includes("publication")) return "publication";
  if (c.includes("study design")) return "study design";
  if (c.includes("animal") || c.includes("in-vitro") || c.includes("in vitro")) return "animal";
  if (c.includes("language")) return "language";
  if (c.includes("date") || c.includes("timeframe") || c.includes("time frame")) return "date";
  return null;
};

// Split on sepRe, ignoring separators inside (), [] or {}.
const splitTopLevel = (text, sepRe) => {
  let depth = 0;
  let masked = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if ("([{".includes(ch)) {
      depth += 1;
      masked += ch;
    } else if (")]}".includes(ch)) {
      depth = Math.max(0, depth - 1);
      masked += ch;
    } else {
      masked += depth ? "\u0000" : ch;
    }
  }
  const re = new RegExp(sepRe.source, sepRe.flags.includes("g") ? sepRe.flags : `${sepRe.flags}g`);
  const pieces = [];
  let last = 0;
  let m;
  while ((m = re.exec(masked)) !== null) {
    if (!m[0].length) {
      re.lastIndex += 1;
      continue;
    }
    pieces.push(text.slice(last, m.index));
    last = m.index + m[0].length;
  }
  pieces.push(text.slice(last));
  return pieces.map((p) => p.trim()).filter(Boolean);
};

const cleanDuplicateReason = (reason) => {
  const kept = splitTopLevel(String(reason || ""), PART_SEP_RE)
    .map((part) =>
      part
        .replace(/\s*\([^()]*\)/g, (m) => (LINKED_PUBLICATION_RE.test(m) ? "" : m))
        .replace(/^[\s,;]+|[\s,;]+$/g, "")
    )
    .filter((part) => part && !LINKED_PUBLICATION_RE.test(part));
  return kept.join("; ") || DUPLICATE_REASON;
};

const parsePart = (text) => {
  const items = splitTopLevel(text, ITEM_SEP_RE)
    .map((t) => [t, itemKey(t)])
    .filter(([, k]) => k);
  const top = text.replace(/\([^()]*\)/g, "");
  const conj = /\s(?:or|and\/or)\s/i.test(top) ? "or" : /,\s*and\s/i.test(top) ? "and" : null;
  const first = text.charAt(0);
  return { text, items, conj, upper: first !== first.toLowerCase(), changed: false };
};

const joinItems = (part) => {
  const texts = part.items.map(([t]) => t);
  let s;
  if (texts.length === 1) s = texts[0];
  else if (part.conj)
    s = texts.slice(0, -1).join(", ") + (texts.length > 2 ? `, ${part.conj} ` : ` ${part.conj} `) + texts[texts.length - 1];
  else s = texts.join(", ");
  if (part.upper && s.charAt(0) === s.charAt(0).toLowerCase()) s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
};

const dedupeCriteriaRows = (rowsInput, isPriority = () => false) => {
  const rows = rowsInput.map((r) => ({ ...r }));
  rows.forEach((r) => {
    if (normText(r.reason_code) === "duplicate") r.reason = cleanDuplicateReason(r.reason);
  });

  const entries = rows.map((r) => {
    const raw = splitTopLevel(String(r.reason || ""), PART_SEP_RE);
    return {
      r,
      type: r.type,
      owner: codeOwner(r.reason_code),
      priority: !!isPriority(r),
      parts: raw.map(parsePart),
      origCount: raw.length,
    };
  });
  const liveItems = (e) => e.parts.reduce((n, p) => n + (p.items.length || 1), 0);
  const keysOf = (e) => new Set(e.parts.flatMap((p) => p.items.map(([, k]) => k)));
  const removeKey = (e, key) => {
    e.parts.forEach((p) => {
      const before = p.items.length;
      p.items = p.items.filter(([, k]) => k !== key);
      if (p.items.length !== before) p.changed = true;
    });
    e.parts = e.parts.filter((p) => p.items.length || !p.changed);
  };

  // 1. Inside one row: drop a part another part already covers, then any
  //    list item already given in an earlier part.
  entries.forEach((e) => {
    const sets = e.parts.map((p) => new Set(p.items.map(([, k]) => k)));
    const within = (a, b) => [...a].every((k) => b.has(k));
    const keep = e.parts.filter((p, i) => {
      const ks = sets[i];
      if (!ks.size) return true;
      return !sets.some((o, j) => j !== i && within(ks, o) && (o.size > ks.size || j < i));
    });
    const seen = new Set();
    keep.forEach((p) => {
      const fresh = p.items.filter(([, k]) => !seen.has(k));
      if (fresh.length !== p.items.length) {
        p.items = fresh;
        p.changed = true;
      }
      p.items.forEach(([, k]) => seen.add(k));
    });
    e.parts = keep.filter((p) => p.items.length || !p.changed);
  });

  // 2. Same item under two codes of one type: it stays under the code it
  //    belongs to, else in the row that already existed, else the first.
  ["EXCLUSION", "INCLUSION"].forEach((type) => {
    const group = entries.filter((e) => e.type === type);
    const allKeys = [];
    group.forEach((e) => keysOf(e).forEach((k) => !allKeys.includes(k) && allKeys.push(k)));
    allKeys.forEach((key) => {
      const holders = group.filter((e) => keysOf(e).has(key));
      if (holders.length < 2) return;
      const itemText = holders[0].parts.flatMap((p) => p.items).find(([, k]) => k === key)[0];
      const owner = (ITEM_OWNERS.find(([, rx]) => rx.test(itemText)) || [null])[0];
      const byOwner = holders.find((e) => owner && e.owner === owner);
      let winner = byOwner || holders.find((e) => e.priority) || holders[0];
      let losers = holders.filter((e) => e !== winner);
      if (!byOwner && losers.length === 1 && liveItems(losers[0]) <= 1 && liveItems(winner) > 1) {
        [winner, losers] = [losers[0], [winner]];
      }
      losers.forEach((e) => {
        if (liveItems(e) > 1) removeKey(e, key);
      });
    });
  });

  // 3. Study design vs publication: an item that clearly belongs to the
  //    other code moves there.
  const pair = { publication: "study design", "study design": "publication" };
  const exclusions = entries.filter((e) => e.type === "EXCLUSION");
  exclusions.forEach((e) => {
    const other = pair[e.owner];
    if (!other) return;
    const target = exclusions.find((t) => t.owner === other);
    if (!target) return;
    [...e.parts].forEach((p) => {
      [...p.items].forEach(([text, key]) => {
        if (!OWNER_RX[other].test(text) || OWNER_RX[e.owner].test(text)) return;
        if (liveItems(e) <= 1) return;
        removeKey(e, key);
        if (!keysOf(target).has(key)) {
          target.parts.push({ text, items: [[text, key]], conj: null, upper: true, changed: true });
        }
      });
    });
  });

  // 4. An exclusion that repeats an inclusion item word for word.
  const inclusionKeys = new Set(entries.filter((e) => e.type === "INCLUSION").flatMap((e) => [...keysOf(e)]));
  exclusions.forEach((e) => {
    [...keysOf(e)].forEach((key) => {
      if (inclusionKeys.has(key) && liveItems(e) > 1) removeKey(e, key);
    });
  });

  entries.forEach((e) => {
    if (!e.parts.some((p) => p.changed) && e.parts.length === e.origCount) return;
    const text = e.parts.map((p) => (p.changed ? joinItems(p) : p.text)).join("; ");
    if (text.trim()) e.r.reason = text;
  });
  return rows;
};

// Generated criteria → merged by code, repeated information removed, sorted.
// isPriority marks rows that already existed (they win ties).
const cleanAndSort = (rowsInput, isPriority) =>
  sortCriteria(dedupeCriteriaRows(mergeByReasonCode(rowsInput), isPriority));

const CriteriaReasonsForm = forwardRef(
    ({ projectId, stageData, protocolMarkdown, onValidityChange, onDataChange, criteriaGenPromise, protocolSavedTick = 0 }, ref) => {
    const navigate = useNavigate();
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);
    const [useProtocol, setUseProtocol] = useState(true);
    const [aiPrompt, setAiPrompt] = useState("");
    const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
    const [popupErrorMessage, setPopupErrorMessage] = useState("");
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
    const [pendingRevision, setPendingRevision] = useState(null);
    const [rows, setRows] = useState(() => [
      makeRow("EXCLUSION"),
      makeRow("INCLUSION"),
      makeRow("EXCLUSION"),
    ]);
    const [aiMode, setAiMode] = useState(true);
    const templateFileRef = useRef(null);
    const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
    const [templateNotice, setTemplateNotice] = useState("");
    const [showUploadConfirm, setShowUploadConfirm] = useState(false);
    const [pendingUploadFile, setPendingUploadFile] = useState(null);
    const [customCodeIds, setCustomCodeIds] = useState(() => new Set());

    // True once the project has criteria — saved ones, or rows the user filled.
    // Criteria are created from the protocol automatically only while this is
    // false (first time); after that only "Update from Protocol" changes them.
    const hasCriteriaRef = useRef(false);
    hasCriteriaRef.current =
      (stageData?.criteria_list?.length ?? 0) > 0 ||
      rows.some((r) => (r.reason || "").trim().length > 0);

  const enterCustom = (id) =>
    setCustomCodeIds((prev) => new Set(prev).add(id));

  const exitCustom = (id) =>
    setCustomCodeIds((prev) => { const n = new Set(prev); n.delete(id); return n; });

  const isCustomCode = (r) =>
    customCodeIds.has(r.id) ||
    (!!r.reason_code && !REASON_CODE_OPTIONS[r.type].includes(r.reason_code));

  // Codes compare case- and space-insensitively ("Publication" = "publication ").
  const codeKey = (c) => (c || "").trim().toLowerCase();

  // Per-type set of reason_codes already in use (for duplicate prevention).
  const usedCodesForType = useMemo(() => {
    const map = { EXCLUSION: new Set(), INCLUSION: new Set() };
    for (const r of rows) {
      if (codeKey(r.reason_code)) map[r.type]?.add(codeKey(r.reason_code));
    }
    return map;
  }, [rows]);

  // A code may be used once per type (Inclusion and Exclusion are separate).
  // True when another row of the same type already has this row's code.
  // Used codes are disabled in the dropdown, so this is reachable only by
  // typing a custom code.
  const codeTakenByOther = (r) =>
    !!codeKey(r.reason_code) &&
    rows.some((o) => o.id !== r.id && o.type === r.type && codeKey(o.reason_code) === codeKey(r.reason_code));

  // A custom code the user typed that another row of the same type already
  // uses. Blocks saving until changed. (Criteria restored because screening
  // uses them are not flagged — those can't be fixed by editing the code.)
  const typedDuplicateCode = (r) => customCodeIds.has(r.id) && codeTakenByOther(r);

    const toCriteriaData = (rowsInput) =>
      (rowsInput || []).map((r, idx) => ({
        ...(r.db_id != null ? { id: r.db_id } : {}),
        order_id: idx + 1,
        criteria: r.type, // EXCLUSION / INCLUSION
        reason_code: r.reason_code || null,
        reason: (r.reason || "").trim(),
        abstract_screening: !!r.abstract_screening,
        full_text_screening: !!r.full_text_screening,
      }));

    // Last saved criteria by db id — used to put back in-use criteria the
    // backend refused to delete.
    const savedRowsRef = useRef(new Map());

    const restoreInUseCriteria = (ids = []) => {
      const missing = ids
        .filter((id) => !rows.some((r) => r.db_id === id))
        .map((id) => savedRowsRef.current.get(id))
        .filter(Boolean);
      if (!missing.length) return 0;

      setRows((prev) =>
        sortCriteria([...prev, ...missing.map((r) => ({ ...r, id: makeId() }))])
      );
      setTemplateNotice(
        `${missing.length} ${missing.length === 1 ? "criterion is" : "criteria are"} used by ` +
        "screening decisions and could not be removed, so " +
        `${missing.length === 1 ? "it was" : "they were"} restored. Your other changes are kept — save again.`
      );
      return missing.length;
    };

    useImperativeHandle(ref, () => ({
      getCriteriaData: () => toCriteriaData(rows),
      restoreInUseCriteria,
    }));

    const [showDecisionFlow, setShowDecisionFlow] = useState(false);

    const decisionFlow = useMemo(() => {
      const activeRows = rows.filter((r) => (r.reason || "").trim().length > 0);
      const exclusions = activeRows.filter((r) => r.type === "EXCLUSION");
      const inclusions = activeRows.filter((r) => r.type === "INCLUSION");

      const stageLabel = (r) =>
        r.abstract_screening && r.full_text_screening
          ? "Abstract + Full-text"
          : r.abstract_screening
          ? "Abstract only"
          : r.full_text_screening
          ? "Full-text only"
          : "Not screened";

      const stripCodePrefix = (code) => (code || "").replace(/^[EI]\d+:\s*/, "");

      const displayText = (r) =>
        r.reason_code ? stripCodePrefix(r.reason_code) : r.reason;

      return {
        exclusions: exclusions.map((r, idx) => ({
          id: r.id,
          number: idx + 1,
          question: `${displayText(r)}?`,
          stage: stageLabel(r),
        })),
        inclusions: inclusions.map((r) => ({
          id: r.id,
          text: displayText(r),
          stage: stageLabel(r),
        })),
      };
    }, [rows]);

    const addRow = (type) => setRows((p) => [...p, makeRow(type)]);

    const updateRow = (id, patch) =>
      setRows((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const removeRow = (id) => setRows((p) => p.filter((r) => r.id !== id));

    const canMoveRowUp = (rowId) => {
      const index = rows.findIndex((r) => r.id === rowId);
      return index > 0;
    };

    const canMoveRowDown = (rowId) => {
      const index = rows.findIndex((r) => r.id === rowId);
      return index >= 0 && index < rows.length - 1;
    };

    const moveRowOrder = (rowId, direction) => {
      setRows((prev) => {
        const index = prev.findIndex((r) => r.id === rowId);
        if (index === -1) return prev;

        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= prev.length) return prev;

        const copy = [...prev];
        [copy[index], copy[targetIndex]] = [copy[targetIndex], copy[index]];
        return copy;
      });
    };

    const onDragEnd = (res) => {
      if (!res.destination || res.destination.index === res.source.index) return;
      setRows((prev) => {
        const next = [...prev];
        const [moved] = next.splice(res.source.index, 1);
        next.splice(res.destination.index, 0, moved);
        return next;
      });
    };

    const isStepValid =
      rows.length > 0 &&
      rows.every((r) => (r.reason || "").trim().length > 0 && !typedDuplicateCode(r));

    useEffect(() => {
      onValidityChange?.(isStepValid);
    }, [isStepValid, onValidityChange]);

    // Notify parent about current criteria data whenever rows change
    useEffect(() => {
      onDataChange?.(toCriteriaData(rows));
    }, [rows, onDataChange]);

    const MODE_DIRECTIVE = {
      add: "Add to the existing criteria. Keep every existing entry exactly as it is and append new entries only. Instruction:",
      revise: "Revise the existing criteria. You may rewrite an entry, add new entries, and remove entries that the instruction makes redundant or wrong. Return the COMPLETE final list — every entry you intend to keep must appear in your answer, with its order_id copied from the Observation exactly. Entries you omit will be deleted, so never drop an entry the instruction did not ask you to remove. Give added entries a null order_id. Instruction:",
      replace: "Start over and generate fresh criteria, discarding what exists. Instruction:",
    };

    const doGenerate = async (mode = "replace") => {
      if (!aiPrompt.trim()) {
        console.warn("Prompt is empty");
        return;
      }

      if (!projectId) {
        console.error("projectId missing");
        return;
      }

      const prev_rows = rows;
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Generating criteria using AI");

      try {
        const res = await axios.post(
          "/pceapi/slr/form/generate-criteria",
          {
            fabric_id: projectId,
            use_protocol: useProtocol,
            prompt: `${MODE_DIRECTIVE[mode]} ${aiPrompt}`,
            sample_size: 500,
          },
          { headers }
        );

        const data = res.data;

        if (data.status === "NotAuthorized") {
          setIsProcessingPopupVisible(false);
          authNavigate("/login", "iam", navigate);
          return;
        }

        if (data.status === "failed") {
          setIsProcessingPopupVisible(false);
          setPopupErrorMessage(data.message || "Generation failed");
          setIsErrorPopupVisible(true);
          return;
        }

        const generated = data.data?.criteria || [];
        const generatedRows = generated.map((c) => ({
          id: makeId(),
          // Backend maps each generated criterion to the saved row it continues
          // (c.id). Keeping it means Save updates that row instead of deleting it.
          db_id: c.id ?? null,
          order_id: c.order_id ?? null,
          type: c.criteria_type,
          reason_code: c.reason_code || null,
          reason: c.reason || "",
          abstract_screening: !!c.abstract_screening,
          full_text_screening: !!c.full_text_screening,
        }));

        // The agent does not reliably echo back db_id or reason_code, so the
        // response is merged onto existing rows instead of replacing them.
        const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

        if (mode === "replace") {
          setRows(cleanAndSort(generatedRows));
          setIsProcessingPopupVisible(false);
          return;
        }

        if (mode === "add") {
          setRows((prev) => {
            const seen = new Set(prev.map((r) => norm(r.reason)));
            const fresh = generatedRows.filter((g) => !seen.has(norm(g.reason)));
            const prevIds = new Set(prev.map((r) => r.id));
            return cleanAndSort([...prev, ...fresh], (r) => prevIds.has(r.id));
          });
          setIsProcessingPopupVisible(false);
          return;
        }

        // revise: entries may be rewritten, added or removed.
        const byOrder = new Map(
          prev_rows.filter((r) => r.order_id != null).map((r) => [r.order_id, r])
        );
        const kept = [];
        const added = [];
        const matchedOrderIds = new Set();

        generatedRows.forEach((g) => {
          const match = g.order_id != null ? byOrder.get(g.order_id) : null;
          if (match) {
            matchedOrderIds.add(match.order_id);
            kept.push({
              ...match,                                   // id, db_id, order_id kept
              type: g.type || match.type,
              reason: g.reason || match.reason,
              reason_code: g.reason_code || match.reason_code,
              abstract_screening: g.abstract_screening,
              full_text_screening: g.full_text_screening,
              _changed:
                norm(g.reason) !== norm(match.reason) ||
                (g.reason_code || "") !== (match.reason_code || ""),
            });
          } else {
            added.push(g);
          }
        });

        const removed = prev_rows.filter(
          (r) => r.order_id != null && !matchedOrderIds.has(r.order_id)
        );

        setIsProcessingPopupVisible(false);
        setPendingRevision({
          nextRows: mergeAndSort([...kept, ...added].map(({ _changed, ...r }) => r)),
          rewritten: kept.filter((r) => r._changed).length,
          added,
          removed,
        });
      } catch (err) {
        console.error("Error generating criteria:", err);
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(
          err?.response?.data?.message ||
          err.message ||
          "Something went wrong while generating"
        );
        setIsErrorPopupVisible(true);
      }
    };

    // Protocol-derived criteria → rows. Empty form: take them as they are.
    // Existing criteria: merge (matching codes get the new reason text appended,
    // new codes are added, nothing is removed, saved ids are kept).
    // Repeated information is then removed across all rows (existing rows win
    // ties), so each code ends up holding only its own conditions.
    const applyProtocolCriteria = (generatedRows) =>
      setRows((prev) => {
        if (!prev.some((r) => (r.reason || "").trim())) return cleanAndSort(generatedRows);
        const prevIds = new Set(prev.map((r) => r.id));
        return sortCriteria(
          dedupeCriteriaRows(mergeGeneratedIntoExisting(prev, generatedRows), (r) => prevIds.has(r.id))
        );
      });

    // "Update from Protocol" button — the only way criteria follow protocol
    // changes after they were first created.
    const handleUpdateFromProtocol = async () => {
      if (!projectId) return;
      setTemplateNotice("");
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Updating criteria from your screening protocol using AI…");

      try {
        const res = await axios.post(
          "/pceapi/slr/form/generate-criteria",
          {
            fabric_id: projectId,
            use_protocol: true,
            prompt:
              `${MODE_DIRECTIVE.add} Bring the criteria in line with the current Screening Protocol: ` +
              "add a criterion for every eligibility rule in the protocol that is not covered yet. " +
              "Put each rule under the one reason code it belongs to — split protocol rows that combine " +
              "study design and publication type into their separate codes — and never repeat a condition " +
              "that an existing entry already covers, even reworded.",
            sample_size: 500,
          },
          { headers }
        );
        const data = res.data;

        if (data.status === "NotAuthorized") {
          authNavigate("/login", "iam", navigate);
          return;
        }
        if (data.status !== "success") {
          setPopupErrorMessage(data.message || "Failed to update criteria from the protocol");
          setIsErrorPopupVisible(true);
          return;
        }

        const generated = data.data?.criteria || [];
        if (!generated.length) {
          setPopupErrorMessage("AI returned no criteria for this protocol. Please try again.");
          setIsErrorPopupVisible(true);
          return;
        }

        applyProtocolCriteria(toGeneratedRows(generated));
        setTemplateNotice(
          "Criteria updated from your screening protocol using AI — new reasons were merged into matching codes. Review before saving."
        );
      } catch (err) {
        console.error("Error updating criteria from protocol:", err);
        setPopupErrorMessage(
          err?.response?.data?.message || err.message || "Failed to update criteria from the protocol"
        );
        setIsErrorPopupVisible(true);
      } finally {
        setIsProcessingPopupVisible(false);
      }
    };

    const onGenerate = () => {
      if (!aiPrompt.trim()) {
        console.warn("Prompt is empty");
        return;
      }
      const hasExisting = rows.some((r) => (r.reason || "").trim().length > 0);
      if (hasExisting) {
        setShowGenerateConfirm(true);
      } else {
        doGenerate("replace");
      }
    };

    const handleDownloadTemplate = async () => {
      try {
        const res = await axios.get("/pceapi/slr/form/reasons-template/download", {
          headers,
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Reasons_Template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const data = err?.response?.data;
        if (data?.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
        setPopupErrorMessage(data?.message || err.message || "Failed to download reasons template");
        setIsErrorPopupVisible(true);
      }
    };

    const applyUploadedCriteria = async (file) => {
      setIsUploadingTemplate(true);
      setIsProcessingPopupVisible(true);
      setProcessingMessage("Reading uploaded reasons template");

      try {
        const fd = new FormData();
        fd.append("reasons_file", file);

        const response = await axios.post(
          "/pceapi/slr/form/upload-reasons-template",
          fd,
          { headers: { ...headers, "Content-Type": "multipart/form-data" } }
        );
        const data = response.data;

        if (data.status === "NotAuthorized") { authNavigate("/login", "iam", navigate); return; }
        if (data.status === "failed") {
          setPopupErrorMessage(data.message || "Failed to read reasons template");
          setIsErrorPopupVisible(true);
          return;
        }

        const uploadedRows = (data.criteria || []).map((c) => ({
          id: makeId(),
          db_id: null,
          type: c.criteria,
          reason_code: c.reason_code || null,
          reason: c.reason || "",
          abstract_screening: !!c.abstract_screening,
          full_text_screening: !!c.full_text_screening,
        }));

        setRows(mergeAndSort(uploadedRows));
        setTemplateNotice(data.message || "");
      } catch (err) {
        setPopupErrorMessage(err?.response?.data?.message || err.message || "Failed to upload reasons template");
        setIsErrorPopupVisible(true);
      } finally {
        setIsUploadingTemplate(false);
        setIsProcessingPopupVisible(false);
      }
    };

    const handleTemplateFileChange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (templateFileRef.current) templateFileRef.current.value = "";

      setTemplateNotice("");
      const hasExisting = rows.some((r) => (r.reason || "").trim().length > 0);
      if (hasExisting) {
        setPendingUploadFile(file);
        setShowUploadConfirm(true);
      } else {
        applyUploadedCriteria(file);
      }
    };

        const lastHandledPromiseRef = useRef(null);

    // Auto-create criteria from the LLM promise fired when the protocol is saved
    // — FIRST TIME ONLY, i.e. while the project has no criteria yet. Once
    // criteria exist, a protocol save never changes them; the user clicks
    // "Update from Protocol" to apply protocol changes.
    // Uses ProcessingPopup (same as manual Generate button) instead of inline spinner.
    useEffect(() => {
      if (!criteriaGenPromise) return;
      if (criteriaGenPromise === lastHandledPromiseRef.current) return;

      lastHandledPromiseRef.current = criteriaGenPromise;

      if (hasCriteriaRef.current) {
        criteriaGenPromise.catch(() => {}); // result not used — no unhandled rejection
        setTemplateNotice(
          "Screening protocol saved. Your criteria were not changed — click “Update from Protocol” to apply the protocol to them."
        );
        return;
      }

      setIsProcessingPopupVisible(true);
      setProcessingMessage("Generating criteria from your screening protocol using AI…");

      criteriaGenPromise
        .then((res) => {
          const data = res.data;
          if (data.status !== "success") throw new Error(data.message || "generation failed");
          const generated = data.data?.criteria || [];
          if (!generated.length) throw new Error("empty response");
          // Saved criteria loaded while AI was running — leave them as they are.
          if (hasCriteriaRef.current) return;
          setRows(cleanAndSort(toGeneratedRows(generated)));
          setTemplateNotice(
            "Criteria created from your screening protocol using AI. Review before saving."
          );
        })
        .catch(() => {
          setTemplateNotice(
            "Could not generate criteria automatically — click “Update from Protocol”, use the AI prompt above, or add them manually."
          );
        })
        .finally(() => {
          setIsProcessingPopupVisible(false);
        });
    }, [criteriaGenPromise]);

    // Protocol re-saved while criteria exist: the parent did not regenerate
    // them, so point the user at the button.
    useEffect(() => {
      if (!protocolSavedTick) return;
      if (hasCriteriaRef.current) {
        setTemplateNotice(
          "Screening protocol saved. Your criteria were not changed — click “Update from Protocol” to apply the protocol to them."
        );
      }
    }, [protocolSavedTick]);

    useEffect(() => {
      if (!stageData?.criteria_list?.length) return;

      // Rows saved before codes dropped the E/I numbering still carry a
      // prefix. 
      const stripLegacyPrefix = (c) =>
        c ? c.replace(/^[EI]\d+\s*:\s*/, "").trim() || null : null;

      const hydratedRows = stageData.criteria_list.map((c, i) => ({
        id: makeId(),
        db_id: c.id ?? null,
        order_id: c.order_id ?? i + 1,
        type: c.criteria, // EXCLUSION / INCLUSION
        reason_code: stripLegacyPrefix(c.reason_code),
        reason: c.reason,
        abstract_screening: c.abstract_screening,
        full_text_screening: c.full_text_screening,
        decision_count: c.decision_count ?? 0,
      }));

      savedRowsRef.current = new Map(
        hydratedRows.filter((r) => r.db_id != null).map((r) => [r.db_id, r])
      );
      setRows(mergeAndSort(hydratedRows));
    }, [stageData]);

    return (
      <div  className="w-100 ev-min-w-0 slr-criteria-root">
        {/* TOP BAR — AI mode toggle + Add buttons (left) / Download-Upload (right, only when OFF) */}
        <div className="d-flex justify-content-between align-items-center mb-3 ms-1 me-3 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="ev-reg-p-dark ev-bold-med">AI mode</span>
              <button
                type="button"
                role="switch"
                aria-checked={aiMode}
                aria-label="Toggle AI mode"
                className={`slr-ai-switch${aiMode ? " active" : ""}`}
                onClick={() => setAiMode((prev) => !prev)}
              >
                <span className="slr-ai-switch-knob" />
              </button>
              {!aiMode && <span className="ev-small-p-dark opacity-75">OFF</span>}
            </div>

            <button
              type="button"
              className="ev-btn-blue-fill-round ev-btn-med"
              onClick={() => addRow("EXCLUSION")}
            >
              + Add Exclusion Criteria
            </button>
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-btn-med"
              onClick={() => addRow("INCLUSION")}
            >
              + Add Inclusion Criteria
            </button>
          </div>

          {/* Right corner: template buttons (AI mode off) + Update from Protocol */}
          <div className="d-flex gap-2 ms-auto">
            {!aiMode && (
              <>
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-med"
                  onClick={handleDownloadTemplate}
                >
                  <i className="bi bi-download me-1" />
                  Download Template
                </button>
                <button
                  type="button"
                  className="ev-btn-dark-blue-fill-round ev-btn-med"
                  onClick={() => templateFileRef.current?.click()}
                  disabled={isUploadingTemplate}
                >
                  <i className="bi bi-upload me-1" />
                  Upload Reasons Template
                </button>
                <input
                  type="file"
                  ref={templateFileRef}
                  className="d-none"
                  accept=".xlsx"
                  onChange={handleTemplateFileChange}
                />
              </>
            )}
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-med"
              onClick={handleUpdateFromProtocol}
              disabled={
                isProcessingPopupVisible ||
                (typeof protocolMarkdown === "string" && !protocolMarkdown.trim())
              }
              title={
                typeof protocolMarkdown === "string" && !protocolMarkdown.trim()
                  ? "Save a screening protocol first"
                  : "Update the criteria from your last saved screening protocol using AI. Existing criteria are kept; new ones are added."
              }
            >
              <i className="bi bi-arrow-repeat me-1" />
              Update from Protocol
            </button>
          </div>
        </div>

        {/* GENERATE USING AI — label row, same left/right margins as prompt row below */}
        {aiMode && (
          <div className="d-flex justify-content-between align-items-center mb-2 ms-1 me-3">
            <span className="ev-reg-p-dark ev-bold-med text-nowrap">
              Generate using AI <i className="bi bi-stars gradient-font6"></i>
            </span>
            <div className="ev-card-2 form-check form-switch py-1 ev-radius-xsmall mb-0">
              <label className="form-check-label ev-small-p-blue d-flex align-items-center gap-5 ev-bold-med">
                Use Protocol
                <input
                  className="form-check-input custom-switch"
                  type="checkbox"
                  id="valueMessageToggle"
                  checked={useProtocol}
                  onChange={(e) => setUseProtocol(e.target.checked)}
                />
              </label>
            </div>
          </div>
        )}

        {/* PROMPT ROW — same left/right margins as label row above */}
        {aiMode && (
          <div className="position-relative mb-4 ms-1 me-3">
            <input
              type="text"
              className="formField4 w-100 slr-input-with-btn"
              placeholder="Add your prompt here"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
            />
            <button
              type="button"
              className="ev-btn-gradient-fill-round ev-btn-reg slr-input-inner-btn ev-radius-xsmall"
              onClick={onGenerate}
            >
              Generate
            </button>
          </div>
        )}

        {templateNotice && (
          <div className="row mb-2 ms-1">
            <div className="col-12">
              <div className="slr-protocol-notice">
                <i className="bi bi-check-circle me-2" />
                <span className="ev-small-p-dark">{templateNotice}</span>
                <button
                  type="button"
                  className="slr-protocol-notice-close"
                  aria-label="Dismiss"
                  onClick={() => setTemplateNotice("")}
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            </div>
          </div>
        )}

        {showDecisionFlow && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1040 }}
              onClick={() => setShowDecisionFlow(false)}
            />
            <div className="slr-decision-flow-panel">
              <div className="slr-decision-flow-header d-flex justify-content-between align-items-start">
                <div>
                  <p className="ev-med-p-dark ev-bold-reg mb-1 d-inline-flex align-items-center gap-2">
                    Decision Flow
                    <span className="ev-message-card3">{decisionFlow.exclusions.length} rules</span>
                  </p>
                  <p className="ev-small-p-dark opacity-75 mb-0">
                    Evaluated in current row order
                  </p>
                </div>
                <button
                  type="button"
                  className="slr-decision-flow-close"
                  aria-label="Close"
                  onClick={() => setShowDecisionFlow(false)}
                >
                  <i className="bi bi-x" />
                </button>
              </div>

              <div className="slr-decision-flow-body scrollable-element">
                <div className="slr-decision-rule start">
                  <i className="bi bi-play-circle-fill me-2" />
                  <span className="ev-small-p-dark ev-bold-med">Start Review</span>
                </div>
                <div className="slr-decision-arrow">↓</div>

                {decisionFlow.exclusions.map((rule) => (
                  <React.Fragment key={rule.id}>
                    <div className="slr-decision-rule exclude">
                      <span className="slr-decision-rule-number">{rule.number}</span>
                      <div className="flex-grow-1">
                        <p className="ev-small-p-dark mb-0">{rule.question}</p>
                        <span className="ev-message-card3">{rule.stage}</span>
                      </div>
                      <span className="ev-alert-card-danger slr-decision-outcome">Exclude</span>
                    </div>
                    <div className="slr-decision-arrow">↓</div>
                  </React.Fragment>
                ))}

                {decisionFlow.inclusions.length > 0 ? (
                  <>
                    <div className="slr-decision-rule">
                      <div className="flex-grow-1">
                        <p className="ev-small-p-dark ev-bold-med mb-1">
                          Meets all inclusion criteria?
                        </p>
                        <ul className="ev-small-p-dark opacity-75 mb-0 ps-3">
                          {decisionFlow.inclusions.map((inc) => (
                            <li key={inc.id}>{inc.text}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="slr-decision-arrow">↓</div>
                    <div className="slr-decision-rule include">
                      <span className="ev-small-p-dark ev-bold-med flex-grow-1">Yes</span>
                      <span className="ev-alert-card-success slr-decision-outcome">Include</span>
                    </div>
                    <div className="slr-decision-rule">
                      <span className="ev-small-p-dark ev-bold-med flex-grow-1">No</span>
                      <span className="ev-alert-card-warning slr-decision-outcome">Needs Manual Review</span>
                    </div>
                  </>
                ) : (
                  <div className="slr-decision-rule include">
                    <span className="ev-small-p-dark ev-bold-med flex-grow-1">
                      Passed all exclusion criteria
                    </span>
                    <span className="ev-alert-card-success slr-decision-outcome">Include</span>
                  </div>
                )}
              </div>

              <div className="slr-decision-flow-footer">
                <p className="ev-xsmall-p-light opacity-75 mb-0">
                  <i className="bi bi-info-circle me-1" />
                  Rule order updates automatically when rows are reordered.
                </p>
              </div>
            </div>
          </>
        )}

        {/* HEADING + View Decision Flow on the same row */}
        <div className="row mb-2">
          <div className="col-12 text-start px-2 d-flex justify-content-between align-items-center">
            <p className="ev-med-p-dark mb-0 ms-4 ev-bold-reg">
              Criteria Reason Actions
            </p>
            {rows.some((r) => (r.reason || "").trim()) && (
              <button
                type="button"
                className="ev-btn-export-design ev-btn-med me-4"
                onClick={() => setShowDecisionFlow(true)}
              >
                <i className="bi bi-diagram-3 me-1" />
                View Decision Flow
              </button>
            )}
          </div>
        </div>

        {/* GRID TABLE */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="criteria">
            {(dp) => (
        <div
          ref={dp.innerRef}
          {...dp.droppableProps}
          className="w-100 scrollable-element ms-2 px-2 slr-criteria-scroll"
        >
          {rows.map((r, idx) => {
            const isExcl = r.type === "EXCLUSION";
            // Exclusions are applied in this order and the first match becomes
            // the record's exclusion reason, so the position is the hierarchy
            // number.
            const exclOrder = isExcl
              ? rows.slice(0, idx + 1).filter((x) => x.type === "EXCLUSION").length
              : null;
            return (
              <Draggable key={r.id} draggableId={r.id} index={idx}>
                {(dr, snap) => (
              <div
                ref={dr.innerRef}
                {...dr.draggableProps}
                className={`slr-criteria-row${snap.isDragging ? " slr-criteria-row-dragging" : ""}`}
              >
                <span
                  {...dr.dragHandleProps}
                  className="slr-criteria-grip"
                  title={isExcl
                    ? `Exclusion #${exclOrder} — applied in this order, first match wins. Drag to reorder.`
                    : "Drag to reorder"}
                >
                  <i className="bi bi-grip-vertical" />
                  {isExcl && (
                    <span className="ev-xxsmall-p-dark d-block text-center">{exclOrder}</span>
                  )}
                </span>
                <div
                  className={`slr-criteria-type slr-criteria-selectWrap d-flex align-items-center ev-radius-xsmall ${
                    isExcl ? "slr-criteria-excl" : "slr-criteria-incl"
                  }`}
                >
                  <div className="w-100 position-relative">
                    <select
                      className="slr-criteria-select w-100"
                      value={r.type}
                        onChange={(e) => {
                        exitCustom(r.id);
                        updateRow(r.id, { type: e.target.value, reason_code: null });
                      }}
                    >
                      <option value="EXCLUSION">Exclusion Criteria</option>
                      <option value="INCLUSION">Inclusion Criteria</option>
                    </select>
                    <i className="bi bi-chevron-down slr-criteria-selectIcon" />
                  </div>
                </div>

                                <div className="slr-criteria-code slr-criteria-selectWrap d-flex align-items-center">
                  <div className="w-100 position-relative">
                    {isCustomCode(r) ? (
                      <>
                        <input
                          className="slr-criteria-select w-100"
                          value={r.reason_code || ""}
                          placeholder="e.g. Wrong dose"
                          autoFocus
                          aria-invalid={typedDuplicateCode(r)}
                          title={typedDuplicateCode(r) ? "Code already in use — choose a different code" : ""}
                          onChange={(e) => updateRow(r.id, { reason_code: e.target.value || null })}
                          onBlur={() => { if (!r.reason_code) exitCustom(r.id); }}
                        />
                        {typedDuplicateCode(r) && (
                          <i
                            className="bi bi-exclamation-circle-fill slr-criteria-selectIcon slr-criteria-code-error"
                            role="img"
                            aria-label="Code already in use"
                            title="Code already in use — choose a different code"
                            style={{ color: "#dc3545", pointerEvents: "auto", cursor: "help" }}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <select
                          className="slr-criteria-select w-100 text-truncate"
                          title={r.reason_code || ""}
                          value={r.reason_code || ""}
                          onChange={(e) => {
                            if (e.target.value === CUSTOM_CODE) {
                              enterCustom(r.id);
                              updateRow(r.id, { reason_code: null });
                              return;
                            }
                            updateRow(r.id, { reason_code: e.target.value || null });
                          }}
                        >
                          <option value="">No code</option>
                          {REASON_CODE_OPTIONS[r.type].map((opt) => (
                            <option
                              key={opt}
                              value={opt}
                              disabled={codeKey(opt) !== codeKey(r.reason_code) && usedCodesForType[r.type]?.has(codeKey(opt))}
                            >
                              {opt.replace(/^[EI]\d+:\s*/, "")}
                              {codeKey(opt) !== codeKey(r.reason_code) && usedCodesForType[r.type]?.has(codeKey(opt)) ? " (in use)" : ""}
                            </option>
                          ))}
                          <option value={CUSTOM_CODE}>+ Custom code…</option>
                        </select>
                        <i className="bi bi-chevron-down slr-criteria-selectIcon" />
                      </>
                    )}
                  </div>
                </div>

                <input
                  className="formField4 slr-criteria-reason"
                  value={r.reason}
                  placeholder="Type reason here"
                  onChange={(e) => updateRow(r.id, { reason: e.target.value })}
                />

                <div className="slr-criteria-flags">
                  <label className="slr-criteria-flag-chip">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      checked={!!r.abstract_screening}
                      onChange={(e) => updateRow(r.id, { abstract_screening: e.target.checked })}
                    />
                    <span className="ev-small-p-dark mb-0">Abstract</span>
                  </label>
                  <label className="slr-criteria-flag-chip">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      checked={!!r.full_text_screening}
                      onChange={(e) => updateRow(r.id, { full_text_screening: e.target.checked })}
                    />
                    <span className="ev-small-p-dark mb-0">Full-text</span>
                  </label>
                </div>

                <div className="slr-criteria-actions">
                  <div className="slr-criteria-reorder">
                    <button
                      type="button"
                      className="template-ordering-btn"
                      onClick={() => moveRowOrder(r.id, "up")}
                      disabled={!canMoveRowUp(r.id)}
                    >
                      <i className="bi bi-caret-up-square"></i>
                    </button>
                    <button
                      type="button"
                      className="template-ordering-btn"
                      onClick={() => moveRowOrder(r.id, "down")}
                      disabled={!canMoveRowDown(r.id)}
                    >
                      <i className="bi bi-caret-down-square"></i>
                    </button>
                  </div>

                  <button
                    type="button"
                    className={`ev-btn-red-fill-round ev-btn-med ev-radius-xsmall p-2 ${r.decision_count > 0 ? "disabled opacity-50" : ""}`}
                    onClick={() => removeRow(r.id)}
                    disabled={r.decision_count > 0}
                    title={
                      r.decision_count > 0
                        ? `Used in ${r.decision_count} screening decision${r.decision_count === 1 ? "" : "s"} — edit instead`
                        : "Delete"
                    }
                  >
                    <i className="bi bi-trash3" />
                  </button>
                </div>
              </div>
                )}
              </Draggable>
            );
          })}
          {dp.placeholder}
          </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* POPUPS */}
        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}

        {isErrorPopupVisible && (
          <ErrorPopup
            onClose={() => setIsErrorPopupVisible(false)}
            message={popupErrorMessage}
          />
        )}

        {/* AI generate — what to do with existing criteria */}
        {showGenerateConfirm && (
          <div className="popup" onClick={() => setShowGenerateConfirm(false)}>
            <div
              className="popup-content slr-genmode-picker px-3"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="ev-med-p-blue ev-bold-med mb-1">
                You already have criteria
              </p>
              <p className="ev-small-p-dark mb-3">
                Choose what AI should do with your{" "}
                {rows.filter((r) => (r.reason || "").trim()).length} existing criteria.
              </p>

              {[
                {
                  key: "add",
                  title: "Keep and add",
                  desc: "Your criteria stay exactly as they are. AI appends new ones based on your prompt.",
                },
                {
                  key: "revise",
                  title: "Keep and revise",
                  desc: "AI rewrites, adds and removes criteria as your prompt asks. Anything it doesn't touch stays as it is, and you review the changes before they apply.",
                },
                {
                  key: "replace",
                  title: "Replace everything",
                  desc: "All current criteria are discarded and regenerated. If you have already run AI screening, the reasons linked to those papers will no longer match.",
                },
              ].map((m) => (
                <button
                  key={m.key}
                  className="slr-genmode-option"
                  onClick={() => {
                    setShowGenerateConfirm(false);
                    doGenerate(m.key);
                  }}
                >
                  <span className="ev-reg-p-dark ev-bold-reg d-block">{m.title}</span>
                  <span className="ev-small-p-dark">{m.desc}</span>
                </button>
              ))}

              <div className="d-flex justify-content-end mt-2">
                <button
                  className="ev-btn-light-fill ev-btn-small"
                  onClick={() => setShowGenerateConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revise — confirm the diff before applying, since it can delete */}
        {pendingRevision && (
          <div className="popup" onClick={() => setPendingRevision(null)}>
            <div className="popup-content slr-genmode-picker" onClick={(e) => e.stopPropagation()}>
              <p className="ev-med-p-blue ev-bold-med mb-1">Review AI changes</p>
              <p className="ev-small-p-dark mb-3">
                {pendingRevision.rewritten} rewritten · {pendingRevision.added.length} added ·{" "}
                {pendingRevision.removed.length} removed
              </p>

              {pendingRevision.removed.length > 0 && (
                <div className="mb-3">
                  <p className="ev-small-p-dark ev-bold-med mb-1">These will be deleted</p>
                  <ul className="ev-small-p-dark ps-3 mb-0 scrollable-element" style={{ maxHeight: 140 }}>
                    {pendingRevision.removed.map((r) => (
                      <li key={r.id}>{r.reason || "(empty)"}</li>
                    ))}
                  </ul>
                </div>
              )}

              {pendingRevision.added.length > 0 && (
                <div className="mb-3">
                  <p className="ev-small-p-dark ev-bold-med mb-1">These will be added</p>
                  <ul className="ev-small-p-dark ps-3 mb-0 scrollable-element" style={{ maxHeight: 140 }}>
                    {pendingRevision.added.map((r) => (
                      <li key={r.id}>{r.reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-2">
                <button className="ev-btn-light-fill ev-btn-small" onClick={() => setPendingRevision(null)}>
                  Discard
                </button>
                <button
                  className="ev-btn-blue-fill-round ev-btn-small"
                  onClick={() => {
                    setRows(pendingRevision.nextRows);
                    setPendingRevision(null);
                  }}
                >
                  Apply changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default CriteriaReasonsForm;